/**
 * THE MEASUREMENT BOOK — the register, and one page in detail.
 *
 * The bound book is the oldest document on an Indian construction site and the
 * one every rupee of a works contract is paid against. Two people, never one:
 * the Site Engineer writes the dimensions he took, the Resident Construction
 * Manager checks the work and signs. Nothing is billable until that second
 * signature exists.
 *
 * WHAT THIS SCREEN ADDS OVER THE PAPER BOOK
 *
 *   - the quantity is computed, so nobody multiplies wrong at seven in the
 *     evening with a torch
 *   - a page that would take a work order past what was ordered is FLAGGED IN
 *     THE REGISTER, before the RCM opens it
 *   - the over-run refusal says what to do about it, on the screen, with the
 *     button to do it
 *   - a certified page cannot be edited, by anybody, ever
 *
 * WHAT IT DELIBERATELY DOES NOT CHANGE
 *
 *   The six columns are the six columns: Description, Nos, Length, Breadth,
 *   Depth, Quantity. A deduction is written as POSITIVE dimensions on a line
 *   flagged as a deduction, exactly as the book has always been kept, because
 *   an engineer who has kept one for twenty years must not have to relearn it.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, CheckCircle2, FileSignature, Minus, Pencil,
  Plus, RotateCcw, Ruler, Send, ShieldCheck, TriangleAlert, Trash2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, dateTime, qty, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select,
  StatusPill, Th, Textarea, sortRows, useSort, useToast,
} from '@/components/ui'
import { AuditTrail } from '@/components/docs/shared'
import { serviceCan } from '../nav'

const n = (v: unknown) => Number(v ?? 0) || 0

export interface MbRow {
  id: string; wo_id: string; site_id: string; site_name: string
  wo_title: string | null; vendor_id: string; vendor_name: string | null
  status: string; measurement_date: string; location_ref: string | null
  measurement_standard: string | null; remarks: string | null
  recorded_by: string | null; recorder_email: string | null
  certified_by: string | null; certifier_email: string | null
  certified_at: string | null; return_reason: string | null
  line_count: number; net_quantity: number; value_measured: number
  would_overrun: boolean
}

export interface MbLine {
  id: string; mb_id: string; wo_line_id: string; sl_no: number
  description: string | null; is_deduction: boolean
  nos: number; dim_l: number | null; dim_b: number | null; dim_d: number | null
  quantity: number; remarks: string | null
}

// ---------------------------------------------------------------------------
// THE REGISTER
// ---------------------------------------------------------------------------
const BLANK = { term: '', status: '', site: '', from: '', to: '' }

export function MeasurementList() {
  const { profile } = useAuth()
  const people = usePeople()
  const { data: allSites } = useSites()
  const [f, setF] = useState({ ...BLANK })
  const { sort, toggle } = useSort('measurement_date', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.entries(f).some(([k, v]) => v !== (BLANK as any)[k])

  const { data: rows, loading } = useQuery<MbRow>(
    'v_wo_measurement_register',
    () => supabase.from('v_wo_measurement_register').select('*'),
    [], { cacheable: false })

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return sortRows(rows.filter((r) => {
      if (f.status && r.status !== f.status) return false
      if (f.site && r.site_id !== f.site) return false
      const d = String(r.measurement_date ?? '').slice(0, 10)
      if (f.from && d < f.from) return false
      if (f.to && d > f.to) return false
      if (t && ![r.id, r.wo_id, r.location_ref, r.vendor_name, r.site_name, r.wo_title]
        .some((v) => String(v ?? '').toLowerCase().includes(t))) return false
      return true
    }), sort, {
      measurement_date: 'date', net_quantity: 'number',
      value_measured: 'number', line_count: 'number',
    })
  }, [rows, f, sort])

  const waiting = rows.filter((r) => r.status === 'SUBMITTED').length
  const blocked = rows.filter((r) => r.status === 'SUBMITTED' && r.would_overrun).length
  const mine = rows.filter((r) => r.recorded_by === profile?.id && r.status === 'RETURNED').length

  const siteOptions = useMemo(
    () => [...new Set(rows.map((r) => r.site_id))]
      .map((id) => ({
        value: id,
        label: (allSites as any[]).find((s) => s.id === id)?.site_name ?? id,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [rows, allSites])

  if (loading) return <Loading label="Opening the Measurement Book…" />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Measurement Book</h1>
          <p className="text-sm text-slate-500">
            {shown.length}{dirty && <> of {rows.length}</>} page
            {rows.length === 1 ? '' : 's'}
            {waiting > 0 && <> · <span className="font-semibold text-amber-700">
              {waiting} awaiting certification</span></>}
          </p>
        </div>
        {serviceCan(profile?.role, 'recordMeasurement') && (
          <Link to="/service/measurements/new" className="btn-primary">
            <Plus className="h-4 w-4" /> Record a measurement
          </Link>
        )}
      </div>

      {blocked > 0 && (
        <Alert tone="warning" title={
          `${blocked} submitted page${blocked === 1 ? '' : 's'} would go past what was ordered`}>
          They are marked <strong>over-run</strong> below. Certifying one will be refused —
          the work may well have been done, but somebody has to sign a Variation Order for it
          first. Open the page to see by how much and on which line.
        </Alert>
      )}
      {mine > 0 && (
        <Alert tone="info" title={`${mine} of your pages came back for correction`}>
          Look for <strong>Returned</strong> below. The reason is on the page.
        </Alert>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Ruler className="mr-1 inline h-3.5 w-3.5" />
        A page is written by the <strong>Site Engineer</strong> with the dimensions he
        measured, and certified by the <strong>Resident Construction Manager</strong>.
        Nobody may do both. Once certified a page cannot be changed — a correction is a new
        page carrying a deduction, exactly as the bound book has always worked.
      </div>

      <Card title={null}>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                       placeholder="Page, work order, location or contractor…"
                       className="min-w-[200px] flex-1" />
          <Select value={f.status} onChange={(e) => set({ status: e.target.value })}
                  className="w-40" placeholder="Any status"
                  options={[...new Set(rows.map((r) => r.status))].sort()
                    .map((s) => ({ value: s, label: s }))} />
          <Select value={f.site} onChange={(e) => set({ site: e.target.value })}
                  className="w-48" placeholder="Every site" options={siteOptions} />
          <Input type="date" className="w-40" value={f.from} title="Measured on or after"
                 onChange={(e) => set({ from: e.target.value })} />
          <Input type="date" className="w-40" value={f.to} title="Measured on or before"
                 onChange={(e) => set({ to: e.target.value })} />
          {dirty && (
            <button className="btn-ghost text-slate-600" onClick={() => setF({ ...BLANK })}>
              Clear
            </button>
          )}
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<Ruler className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'Nothing measured yet'}
                      message={dirty
                        ? `${rows.length} page(s) are hidden by the filters.`
                        : 'Pages appear here as soon as an engineer records one against a live work order.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Page</Th>
                  <Th id="wo_id" sort={sort} onSort={toggle}>Work order</Th>
                  <Th id="measurement_date" sort={sort} onSort={toggle}>Measured</Th>
                  <th>Location</th>
                  <Th id="line_count" sort={sort} onSort={toggle} align="right">Entries</Th>
                  <Th id="value_measured" sort={sort} onSort={toggle} align="right">At contract rates</Th>
                  <th>Recorded by</th>
                  <th>Certified by</th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/measurements/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                    </td>
                    <td className="whitespace-nowrap">
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="text-amber-700 hover:underline">{r.wo_id}</Link>
                      <span className="block max-w-[180px] truncate text-[11px] text-slate-400">
                        {r.vendor_name}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">{ddmmyyyy(r.measurement_date)}</td>
                    <td className="max-w-[200px] truncate">{r.location_ref ?? '—'}</td>
                    <td className="num">{r.line_count}</td>
                    <td className="num whitespace-nowrap">{rupees(r.value_measured)}</td>
                    <td className="max-w-[140px] truncate text-xs">
                      {people.name({ id: r.recorded_by, email: r.recorder_email })}
                    </td>
                    <td className="max-w-[140px] truncate text-xs">
                      {r.certified_by
                        ? people.name({ id: r.certified_by, email: r.certifier_email })
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={r.status} />
                      {r.would_overrun && r.status !== 'CERTIFIED' && (
                        <span className="pill-red ml-1" title="This page goes past what was ordered">
                          over-run
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ONE PAGE
// ---------------------------------------------------------------------------
interface OverrunRow {
  wo_line_id: string; sl_no: number; description: string; uom: string
  allowed: number; already: number; this_book: number; over_by: number
}

export function MeasurementDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const people = usePeople()
  const navigate = useNavigate()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [returning, setReturning] = useState(false)
  const [reason, setReason] = useState('')
  // 0173. An uncertified page held its order open for ever — MB_IN_CHAIN is a
  // close-out blocker and nothing on earth could clear it.
  const [withdrawing, setWithdrawing] = useState(false)
  const [why, setWhy] = useState('')

  const { data: rows, loading, refresh } = useQuery<MbRow>(
    `mb_${id}`,
    () => supabase.from('v_wo_measurement_register').select('*').eq('id', id),
    [id], { cacheable: false })

  const { data: lines, refresh: refreshLines } = useQuery<MbLine>(
    `mb_lines_${id}`,
    () => supabase.from('wo_measurement_lines').select('*').eq('mb_id', id).order('sl_no'),
    [id], { cacheable: false })

  const { data: woLines } = useQuery<any>(
    `mb_wolines_${id}`,
    () => supabase.from('v_wo_line_progress').select('*')
      .eq('wo_id', rows[0]?.wo_id ?? '__none__').order('sl_no'),
    [rows[0]?.wo_id], { cacheable: false, idField: 'wo_line_id',
                        enabled: Boolean(rows[0]?.wo_id) })

  /**
   * The over-run, asked of the DATABASE rather than worked out here.
   *
   * fn_mb_overrun runs the identical arithmetic the certification trigger runs.
   * Recomputing it in TypeScript would have been easy and would have drifted
   * the first time somebody changed one and not the other — and the way you
   * find out is an RCM being refused by a message that contradicts the screen
   * he is looking at.
   */
  const { data: overrun } = useQuery<OverrunRow>(
    `mb_overrun_${id}_${rows[0]?.status}_${lines.length}`,
    () => supabase.rpc('fn_mb_overrun', { p_mb_id: id }) as any,
    [id, rows[0]?.status, lines.length], { cacheable: false, idField: 'wo_line_id' })

  /**
   * IS A VARIATION ALREADY OPEN ON THIS ORDER? — added 16-09-2026.
   *
   * It decides what the remedy button does. rpc_vo_raise refuses a second one
   * while a first is in progress, so offering "raise a variation" against an
   * order that already has one would be a button that always fails. When one is
   * open the button adds the over-run to THAT variation instead, which is also
   * the right answer: two variations covering the same extra work is exactly
   * what the one-at-a-time rule exists to stop.
   */
  const { data: vpos } = useQuery<any>(
    `mb_vpos_${id}_${rows[0]?.wo_id ?? ''}`,
    () => supabase.from('v_wo_variation_position').select('*')
      .eq('wo_id', rows[0]?.wo_id ?? '__none__'),
    [rows[0]?.wo_id], { cacheable: false, idField: 'wo_id',
                        enabled: Boolean(rows[0]?.wo_id) })

  if (loading) return <Loading label="Reading the page…" />
  const m = rows[0]
  if (!m) {
    return <EmptyState icon={<Ruler className="h-8 w-8" />} title="Not found"
                       message="No such page, or it belongs to a site you cannot see." />
  }

  const iRecorded = m.recorded_by === profile?.id
  const canCertify = serviceCan(profile?.role, 'certifyMeasurement')
  const canRecord = serviceCan(profile?.role, 'recordMeasurement')
  const editable = ['DRAFT', 'RETURNED'].includes(m.status)

  /*
    WHO MAY ABANDON A PAGE — 0173, board 22-09-2026.

    The engineer who took it, plus Accounts and the administrator; and only
    while it is still his. A CERTIFIED page is never withdrawn — it is what two
    men agreed was built on a particular day, and the remedy for a wrong one is
    a LATER page carrying the correction as a deduction, so that both sheets
    stay on the file. The database refuses it in those words; the button is not
    drawn either, so nobody discovers the rule by being told off.
  */
  const canWithdraw = editable
    && (iRecorded || ['admin', 'accounts_spoc'].includes(profile?.role ?? ''))

  async function call(fn: string, args: Record<string, unknown>, ok: string) {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc(fn, args)
      if (error) throw new Error(error.message)
      toast.success(String(data ?? ok))
      setReturning(false); setReason('')
      setWithdrawing(false); setWhy('')
      refresh(); refreshLines()
    } catch (e) {
      // The over-run refusal is several lines long and ends with what to do
      // about it. It must not be truncated into a one-line toast.
      toast.error(e instanceof Error ? e.message : 'Could not do that')
    } finally { setBusy(false) }
  }

  const byWoLine = (wid: string) => (woLines as any[]).find((w) => w.wo_line_id === wid)

  // ---- THE REMEDY, AS A BUTTON RATHER THAN A SIGNPOST --------------------
  //
  // It used to link to the variations register, which told a buyer where to go
  // and then made him start from nothing: choose the order he was just looking
  // at, type a reason describing the page he was just reading, find the line,
  // and key in a quantity the screen above had already worked out to three
  // decimal places. Every one of those is a chance to mistype the number that
  // decides what a contractor is paid.
  //
  // So the button now raises the variation and takes him to it with the
  // over-run already on it. He is left with the two decisions that are
  // genuinely his: is this reason right, and is this really the quantity we
  // owe. Adding further lines, editing and cancelling all work as they do on
  // any other variation.
  const mayRaise = serviceCan(profile?.role, 'raiseVariation')
  const openVo: string | null = (vpos as any[])[0]?.open_variation_id ?? null
  const openVoStatus: string | null = (vpos as any[])[0]?.open_variation_status ?? null
  /** An open variation can only be ADDED TO while it is still its raiser's. */
  const openVoEditable = Boolean(openVo && ['DRAFT', 'RETURNED'].includes(openVoStatus ?? ''))

  /**
   * The reason, written from the page itself.
   *
   * Every approver's first question is "why", and the honest answer here is a
   * fact rather than a sentiment: this page measured this much more of this
   * item than the order allows. Pre-written, and editable on the next screen —
   * a buyer who wants to add "agreed at site with the PM on the 12th" should.
   */
  function overrunReason(): string {
    const where = m.location_ref ? ` (${m.location_ref})` : ''
    const what = (overrun as OverrunRow[]).map(
      (o) => `${o.description} — ${qty(o.over_by)} ${o.uom} past the ${qty(o.allowed)} allowed`
    ).join('; ')
    return `Over-run measured on ${m.id}${where}: ${what}. `
         + 'Raised so the work already measured can be certified and billed.'
  }

  async function raiseForOverrun() {
    // Already one open on this order: add to it rather than fail against the
    // one-at-a-time rule.
    if (openVo) {
      navigate(openVoEditable
        ? `/service/variations/${openVo}/edit?from=${encodeURIComponent(m.id)}`
        : `/service/variations/${openVo}`)
      return
    }
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_vo_raise',
        { p_wo_id: m.wo_id, p_reason: overrunReason() })
      if (error) throw new Error(error.message)
      const vo = String(data)
      toast.success(`${vo} raised, with the over-run already on it.`)
      navigate(`/service/variations/${vo}/edit?from=${encodeURIComponent(m.id)}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not raise the variation')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <Link to="/service/measurements"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> The Measurement Book
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{m.id}</h1>
          <p className="text-sm text-slate-600">{m.location_ref ?? 'Site measurement'}</p>
          <p className="mt-1 text-xs text-slate-500">
            {m.site_name} · <Link to={`/service/work-orders/${m.wo_id}`}
                                  className="text-amber-700 hover:underline">{m.wo_id}</Link>
            {' '}· {m.vendor_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={m.status} />
          {editable && canRecord && (iRecorded || profile?.role === 'admin') && (
            <Link to={`/service/measurements/${m.id}/edit`} className="btn-secondary btn-sm">
              <Pencil className="h-4 w-4" /> Edit
            </Link>
          )}
          {canWithdraw && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    title={`Abandon this page so ${m.wo_id} is not held open by it`}
                    onClick={() => { setWhy(''); setWithdrawing(true) }}>
              <Trash2 className="h-4 w-4" /> Withdraw
            </button>
          )}
          {editable && canRecord && (
            <button className="btn-primary btn-sm" disabled={busy}
                    onClick={() => void call('rpc_mb_submit', { p_mb_id: m.id },
                                             'Submitted.')}>
              <Send className="h-4 w-4" /> Submit for certification
            </button>
          )}
          {m.status === 'SUBMITTED' && canCertify && !iRecorded && (
            <>
              <button className="btn-primary btn-sm" disabled={busy}
                      onClick={() => void call('rpc_mb_certify', { p_mb_id: m.id },
                                               'Certified.')}>
                <ShieldCheck className="h-4 w-4" /> Certify
              </button>
              <button className="btn-secondary btn-sm" disabled={busy}
                      onClick={() => setReturning(true)}>
                <RotateCcw className="h-4 w-4" /> Send back
              </button>
            </>
          )}
        </div>
      </div>

      {/* ---------- the one control the module rests on ---------- */}
      {m.status === 'SUBMITTED' && canCertify && iRecorded && (
        <Alert tone="warning" title="You recorded this page yourself">
          Certification means somebody <em>other than the measurer</em> has checked the work —
          that is the whole of what the signature is worth. Another Resident Construction
          Manager, or the administrator, has to sign this one.
        </Alert>
      )}

      {m.status === 'RETURNED' && m.return_reason && (
        <Alert tone="warning" title="Sent back by the Resident Construction Manager">
          {m.return_reason}
        </Alert>
      )}

      {/* ---------- the over-run, and the remedy ---------- */}
      {overrun.length > 0 && m.status !== 'CERTIFIED' && (
        <Card title={
          <span className="flex items-center gap-1.5 text-red-800">
            <TriangleAlert className="h-4 w-4" />
            This page goes past what was ordered
          </span>}
          subtitle="It cannot be certified as it stands. The work may well have been done — but somebody has to sign for it first.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>Line</th>
                  <th className="num">Ordered &amp; varied</th>
                  <th className="num">Already certified</th>
                  <th className="num">This page</th>
                  <th className="num">Over by</th>
                </tr>
              </thead>
              <tbody>
                {overrun.map((o) => (
                  <tr key={o.wo_line_id}>
                    <td>{o.sl_no}</td>
                    <td className="max-w-[260px]">{o.description}</td>
                    <td className="num whitespace-nowrap">{qty(o.allowed)} {o.uom}</td>
                    <td className="num whitespace-nowrap">{qty(o.already)} {o.uom}</td>
                    <td className="num whitespace-nowrap">{qty(o.this_book)} {o.uom}</td>
                    <td className="num whitespace-nowrap font-bold text-red-700">
                      {qty(o.over_by)} {o.uom}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2.5">
            <p className="text-sm font-semibold text-amber-900">What to do about it</p>
            <p className="mt-0.5 text-xs text-amber-800">
              The engineer is not wrong to have measured it. If the work was genuinely done,
              Procurement raises a <strong>Variation Order</strong> on {m.wo_id} for the extra
              quantity; once it is approved this page certifies unchanged, with nothing
              retyped. What must not happen is an over-run quietly becoming a payment.
            </p>
            {mayRaise ? (
              <>
                <button className="btn-primary btn-sm mt-2" disabled={busy}
                        onClick={() => void raiseForOverrun()}>
                  <Plus className="h-4 w-4" />
                  {!openVo
                    ? `Raise the variation on ${m.wo_id}`
                    : openVoEditable
                      ? `Add this to ${openVo}`
                      : `${openVo} is already with an approver`}
                </button>
                <p className="mt-1.5 text-[11px] text-amber-800">
                  {!openVo
                    ? 'It opens with the over-run already on it and a reason written from this page. '
                      + 'Check both, add anything else the contractor is owed, then submit.'
                    : openVoEditable
                      ? `${openVo} is already open on this order, so the over-run is added to it `
                        + 'rather than a second one being raised.'
                      : `${openVo} is open on this order and has gone up the chain. It has to be `
                        + 'approved, returned or rejected before another can be raised.'}
                </p>
              </>
            ) : (
              <>
                <Link to="/service/variations" className="btn-secondary btn-sm mt-2">
                  <Plus className="h-4 w-4" /> Variation Orders
                </Link>
                <p className="mt-1.5 text-[11px] text-amber-800">
                  Raising one is Procurement&rsquo;s. Tell them this page is waiting on it —
                  they can raise it from here in one click.
                </p>
              </>
            )}
          </div>
        </Card>
      )}

      {m.status === 'CERTIFIED' && (
        <Alert tone="success" title={
          `Certified by ${people.name({ id: m.certified_by, email: m.certifier_email })}`}>
          {m.certified_at && <>{dateTime(m.certified_at)}. </>}
          This page is now evidence and cannot be changed. If something on it is wrong, record
          a <strong>new page carrying a deduction</strong> — that is how the Measurement Book
          has always been corrected, and it leaves both figures visible.
        </Alert>
      )}

      <Card title="The page">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Measured on', ddmmyyyy(m.measurement_date)],
            ['Location', m.location_ref ?? '—'],
            ['Standard applied', m.measurement_standard ?? '—'],
            ['Recorded by', people.name({ id: m.recorded_by, email: m.recorder_email })],
          ].map(([k, v]) => (
            <div key={String(k)}>
              <dt className="text-xs font-medium text-slate-500">{k}</dt>
              <dd className="mt-0.5 break-words text-sm text-slate-800">{v}</dd>
            </div>
          ))}
        </dl>
        {m.remarks && (
          <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">{m.remarks}</p>
        )}
      </Card>

      {/* ---------- the six columns ---------- */}
      <Card title="Entries"
            subtitle="Description · Nos · Length · Breadth · Depth · Quantity — the six columns of the bound book. A deduction is written with POSITIVE dimensions and subtracts itself.">
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Against</th>
                <th>Description</th>
                <th className="num">Nos</th>
                <th className="num">Length</th>
                <th className="num">Breadth</th>
                <th className="num">Depth</th>
                <th className="num">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const w = byWoLine(l.wo_line_id)
                return (
                  <tr key={l.id} className={l.is_deduction ? 'bg-red-50/50' : undefined}>
                    <td>{l.sl_no}</td>
                    <td className="max-w-[180px] truncate text-xs">
                      {w ? `${w.sl_no}. ${w.description}` : '—'}
                    </td>
                    <td className="max-w-[240px]">
                      {l.is_deduction && (
                        <span className="pill-red mr-1"><Minus className="h-3 w-3" /> deduct</span>
                      )}
                      {l.description ?? (w?.description ?? '—')}
                      {l.remarks && (
                        <span className="block text-[11px] italic text-slate-400">{l.remarks}</span>
                      )}
                    </td>
                    <td className="num">{qty(l.nos)}</td>
                    <td className="num">{l.dim_l == null ? '—' : qty(l.dim_l)}</td>
                    <td className="num">{l.dim_b == null ? '—' : qty(l.dim_b)}</td>
                    <td className="num">{l.dim_d == null ? '—' : qty(l.dim_d)}</td>
                    <td className={n(l.quantity) < 0
                      ? 'num whitespace-nowrap font-semibold text-red-700'
                      : 'num whitespace-nowrap font-semibold'}>
                      {qty(l.quantity)} {w?.uom ?? ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300">
                <td colSpan={7} className="text-right text-sm font-semibold">
                  Net measured on this page, at contract rates
                </td>
                <td className="num whitespace-nowrap text-sm font-bold">
                  {rupees(m.value_measured)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <Modal open={returning} onClose={() => setReturning(false)}
             title="Send this measurement back"
             subtitle="It goes back to the engineer who recorded it, who can correct it and submit it again."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setReturning(false)}>Cancel</button>
                 <button className="btn-primary" disabled={busy || reason.trim().length < 4}
                         onClick={() => void call('rpc_mb_return',
                           { p_mb_id: m.id, p_reason: reason.trim() }, 'Sent back.')}>
                   Send it back
                 </button>
               </>
             }>
        <p className="mb-2 text-sm text-slate-600">
          Say why. Without a reason the engineer has to guess, and he will measure it the same
          way again.
        </p>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="The soffit of the beam has been measured twice — once here and once on MB-T05-20260910-002." />
      </Modal>

      {/* ------------------------------------------------------------------
          ABANDONING A PAGE — 0173.

          The point the modal has to make is that this is NOT the way to fix a
          wrong measurement. It is the way to drop a page that should never
          have been started — the wrong order, the wrong drain, a duplicate.
          A page that is merely wrong gets corrected and submitted again, and a
          page that has been certified is corrected by a later one. Saying so
          here is cheaper than saying it to an engineer afterwards.
      ------------------------------------------------------------------ */}
      <Modal open={withdrawing} onClose={() => setWithdrawing(false)}
             title="Withdraw this measurement"
             subtitle={`${m.id} · ${m.wo_id}`}
             footer={
               <>
                 <button className="btn-secondary" disabled={busy}
                         onClick={() => setWithdrawing(false)}>
                   Keep it
                 </button>
                 <button className="btn-warning" disabled={busy || why.trim().length < 10}
                         onClick={() => void call('rpc_mb_withdraw',
                           { p_mb_id: m.id, p_reason: why.trim() }, 'Withdrawn.')}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted, and no quantity moves">
            The page and its entries stay in the book, marked withdrawn. It was never
            certified, so it has never counted towards anything — the measured quantities on{' '}
            {m.wo_id} are exactly where they were.{' '}
            <strong>The order is no longer held open by it</strong>, which matters at
            close-out: an uncertified page blocks it.
          </Alert>
          <Alert tone="warning" title="Only for a page that should not exist">
            If the measurement is simply <em>wrong</em>, correct it and submit it again —
            that is what Edit is for. Withdraw this one only if it was taken against the
            wrong order or the wrong work, or duplicates another page.
          </Alert>
          <Field label="Why is it being withdrawn"
                 hint="At least a short sentence. It stays in the book, and this is the only thing that will explain why it was taken and then abandoned.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="e.g. Measured against the wrong drain — re-measured the same afternoon on MB-T05-20260922-004." />
          </Field>
          {why.trim().length > 0 && why.trim().length < 10 && (
            <p className="text-xs text-amber-700">
              A few more words — {10 - why.trim().length} to go.
            </p>
          )}
        </div>
      </Modal>

      <AuditTrail docType="MB" docId={m.id} />
    </div>
  )
}
